import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  IconCamera,
  IconLoader2,
  IconLock,
  IconLockOpen,
  IconChevronRight,
  IconStarFilled,
  IconFlag3Filled,
  IconEyeOff,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { timeAgo, formatDateTime24h } from "@/lib/time";
import { PARAM_GROUPS } from "@/domain/calibration-params";
import type { NvpParameter } from "@/domain/calibration-params";
import { ParamComparisonTable } from "@/components/param-comparison-table";
import { groupAction } from "@/components/calibration-live-controls";
import type { WriteControls, WriteSource } from "@/components/calibration-live-controls";
import type { CalibrationActions } from "@/hooks/use-calibration-actions";
import type { Action } from "@/providers/auth-provider";
import { asCalibrationId } from "@/types/identifiers";
import type { CalibrationState } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type Calibration = components["schemas"]["CalibrationResponse"];

const STATE_LABEL: Record<CalibrationState, string> = {
  initial: "Initial",
  draft: "Draft",
  golden: "Golden",
};

interface CalibrationCardProps {
  cal: Calibration;
  actions: CalibrationActions;
  can: (action: Action) => boolean;
  isUploading: boolean;
  onPhotoClick: () => void;
  readOnly?: boolean;
  compareParams?: NvpParameter[];
  compareLabel?: string;
  compareWarning?: string;
  /** Live column and per-group write buttons; only the golden is a write source. */
  controls?: WriteControls;
}

function CollapsibleParams({
  parameters,
  stateLabel,
  nvpTimestamp,
  fetchedAt,
  fetchedBy,
  compareParams,
  compareLabel,
  compareWarning,
  controls,
  writeSource,
}: {
  parameters: NvpParameter[];
  stateLabel: string;
  nvpTimestamp: string;
  fetchedAt: string;
  fetchedBy: string;
  compareParams?: NvpParameter[];
  compareLabel?: string;
  compareWarning?: string;
  controls?: WriteControls;
  writeSource?: WriteSource;
}) {
  const [open, setOpen] = useState(false);
  const byName = new Map(parameters.map((p) => [p.Name, p]));
  const compareByName = compareParams
    ? new Map(compareParams.map((p) => [p.Name, p]))
    : null;
  const totalParams = PARAM_GROUPS.reduce((sum, g) => sum + g.params.length, 0);

  return (
    <div className="mt-1.5">
      <div className="space-y-0.5">
        <button
          onClick={() => setOpen(!open)}
          className="text-xs text-muted-foreground/70 hover:text-muted-foreground flex items-center gap-1 py-1"
        >
          <IconChevronRight
            className={`h-3 w-3 transition-transform duration-150 ${open ? "rotate-90" : ""}`}
          />
          {totalParams} parameters
        </button>
        <div className="text-xs text-muted-foreground/50">
          MD4 Cal: {formatDateTime24h(nvpTimestamp)}
        </div>
        <div className="text-xs text-muted-foreground/60">
          {timeAgo(fetchedAt)} by {fetchedBy}
        </div>
      </div>
      {open && (
        <div className="mt-2 space-y-3">
          {compareWarning && (
            <div className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-md">
              {compareWarning}
            </div>
          )}
          {compareByName ? (
            <div className="overflow-x-auto">
              <ParamComparisonTable
                baseParams={compareByName}
                compareParams={byName}
                baseLabel={compareLabel!}
                compareLabel="Golden"
                diffColors={{
                  base: "text-blue-600 dark:text-blue-400",
                  compare: "text-amber-600 dark:text-amber-400",
                }}
                liveParams={controls?.live}
                groupActions={
                  controls
                    ? (group) => ({ compare: groupAction(controls, group, writeSource, byName) })
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <ParamComparisonTable
                baseParams={byName}
                baseLabel={stateLabel}
                liveParams={controls?.live}
                groupActions={
                  controls
                    ? (group) => ({ base: groupAction(controls, group, writeSource, byName) })
                    : undefined
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CalibrationThumbnail({ src, onClick }: { src: string; onClick: () => void }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <button onClick={onClick} className="shrink-0 self-start relative w-14 sm:w-16">
      {!loaded && (
        <div className="absolute inset-0 rounded-md bg-muted animate-pulse" />
      )}
      <img
        src={src}
        alt="Calibration photo"
        onLoad={() => setLoaded(true)}
        className={`h-full w-full rounded-md object-cover ring-1 ring-border aspect-[3/4] transition-opacity ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </button>
  );
}

export function CalibrationCard({ cal, actions, can, isUploading, onPhotoClick, readOnly = false, compareParams, compareLabel, compareWarning, controls }: CalibrationCardProps) {
  const calId = asCalibrationId(cal.id);
  const writeSource = cal.state === "golden" ? controls?.golden : undefined;
  const isSigned = !!cal.signed_off_at;
  const canEdit = !readOnly;

  const borderColor = cal.all_values_zero
    ? "border-l-amber-500"
    : cal.state === "golden"
      ? "border-l-amber-400"
      : cal.state === "initial"
        ? "border-l-blue-400"
        : isSigned
          ? "border-l-primary"
          : "border-l-border";

  const cardBg = cal.all_values_zero
    ? "bg-amber-50 dark:bg-amber-950/30"
    : "bg-card";

  return (
    <div className={`border-l-3 pl-5 pr-5 py-4 flex gap-4 rounded-r-lg ${borderColor} ${cardBg}`}>
      <div className="flex-1 min-w-0">
        {cal.all_values_zero && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-md bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200">
            <IconAlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-xs font-medium">All calibration values are zero</span>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={cal.source === "NVP" ? "default" : "outline"} className="text-[10px] uppercase tracking-wide">
            {cal.source}
          </Badge>
          {canEdit && can("manage-calibrations") && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => actions.handleSetState(calId, "initial")}
                    disabled={actions.isSettingState}
                    className={`h-6 px-2 text-[10px] uppercase tracking-wide ${
                      cal.state === "initial"
                        ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400"
                        : "text-muted-foreground/40 hover:text-muted-foreground/70"
                    }`}
                  >
                    <IconFlag3Filled className="mr-1 h-3 w-3" />
                    Initial
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {cal.state === "initial" ? "Remove initial" : "Mark as initial"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => actions.handleSetState(calId, "golden")}
                    disabled={actions.isSettingState}
                    className={`h-6 px-2 text-[10px] uppercase tracking-wide ${
                      cal.state === "golden"
                        ? "bg-amber-100 text-amber-600 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400"
                        : "text-muted-foreground/40 hover:text-muted-foreground/70"
                    }`}
                  >
                    <IconStarFilled className="mr-1 h-3 w-3" />
                    Golden
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {cal.state === "golden" ? "Remove golden" : "Mark as golden"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => actions.handleToggleHidden(calId)}
                    disabled={actions.isTogglingHidden}
                    className="h-6 w-6 p-0 rounded-full text-muted-foreground/40 hover:text-muted-foreground/70"
                  >
                    <IconEyeOff className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Hide</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>
        {(isSigned || cal.state === "golden") && (
          <div className="mt-2 flex items-center gap-2">
            {isSigned ? (
              <>
                <span className="text-[11px] font-medium text-primary flex items-center gap-1">
                  ✓ Signed off by {cal.signed_off_by}
                </span>
                {canEdit && can("sign-off") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={() => actions.handleUnlock(calId)}
                    disabled={actions.isUnlocking}
                  >
                    <IconLockOpen className="h-3 w-3" />
                  </Button>
                )}
              </>
            ) : canEdit && can("sign-off") ? (
              <Button
                variant="outline"
                size="sm"
                className="h-6 px-3 text-xs"
                onClick={() => actions.handleSignOff(calId)}
                disabled={actions.isSigningOff || actions.hasSignedOff}
              >
                <IconLock className="mr-1 h-3 w-3" />
                Sign off
              </Button>
            ) : null}
          </div>
        )}
        <CollapsibleParams
          parameters={cal.parameters as unknown as NvpParameter[]}
          stateLabel={STATE_LABEL[cal.state]}
          nvpTimestamp={cal.nvp_timestamp}
          fetchedAt={cal.fetched_at}
          fetchedBy={cal.fetched_by}
          compareParams={compareParams}
          compareLabel={compareLabel}
          compareWarning={compareWarning}
          controls={controls}
          writeSource={writeSource}
        />
      </div>

      {cal.photo?.thumbnail_url ? (
        <CalibrationThumbnail
          src={cal.photo.thumbnail_url}
          onClick={onPhotoClick}
        />
      ) : isUploading ? (
        <div className="shrink-0 self-start w-14 sm:w-16 aspect-[3/4] rounded-md border border-border flex items-center justify-center">
          <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
        </div>
      ) : canEdit ? (
        <button
          onClick={onPhotoClick}
          className="shrink-0 self-start w-14 sm:w-16 aspect-[3/4] rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground/40 hover:text-muted-foreground/70 hover:border-muted-foreground/40 transition-colors"
        >
          <IconCamera className="h-5 w-5" />
        </button>
      ) : null}
    </div>
  );
}
